import { useEffect, useState } from 'react';
import useNCZICommands from './useNCZICommands';
import useNotification from '../../../Hooks/useNotification.hook';
import useTranslation from '../../../Hooks/useTranslation';
import { Command, ExecutedCommand, FinishedCommand } from '../Commands/Command';

interface useNCZIUpdateProps {
	onFinish?: (id: string, result: any) => void;
	onError?: (id: string, message: string) => void;
	hideSuccess?: boolean;
	hideError?: boolean;
}

export default function useNCZICommandProcess(props: useNCZIUpdateProps) {
	const { onFinish, onError, hideSuccess = false, hideError = false } = props;
	const [processingCommandId, setProcessingCommandId] = useState<
		string | undefined
	>(undefined);
	const [isProcessing, setIsProcessing] = useState<boolean>(false);
	const { commands, execute } = useNCZICommands({
		hideError,
	});
	const [finishedCommands, setFinishedCommands] = useState<string[]>([]);
	const { showNotificationSuccess } = useNotification();
	const { t } = useTranslation();

	useEffect(() => {
		if (processingCommandId && commands[processingCommandId]) {
			const command = commands[processingCommandId];
			if (finishedCommands.indexOf(processingCommandId) === -1) {
				if (command.state === 'done') {
					if (!hideSuccess) {
						showNotificationSuccess({
							content: t('nczi.result.updateSuccess'),
						});
					}
					const finishedCommand = command as FinishedCommand;
					onFinish?.(finishedCommand.id, finishedCommand.result);
					setFinishedCommands(prevState => {
						const newState = [...prevState];
						newState.push(finishedCommand.id);
						return newState;
					});
					setIsProcessing(false);
					setProcessingCommandId(undefined);
				}
				if (command.state === 'offline' || command.state === 'manual') {
					if (command.state === 'offline') {
						showNotificationSuccess({
							content: t('nczi.result.offline'),
						});
					}
					const finishedCommand = command as FinishedCommand;
					onFinish?.(finishedCommand.id, finishedCommand.result);
					setFinishedCommands(prevState => {
						const newState = [...prevState];
						newState.push(finishedCommand.id);
						return newState;
					});
					setIsProcessing(false);
					setProcessingCommandId(undefined);
				}
				if (command.state === 'error') {
					const finishedCommand = command as FinishedCommand;
					onError?.(finishedCommand.id, finishedCommand.errorMessage ?? '');
					setFinishedCommands(prevState => {
						const newState = [...prevState];
						newState.push(finishedCommand.id);
						return newState;
					});
					setIsProcessing(false);
					setProcessingCommandId(undefined);
				}
			}
		}
	}, [commands, processingCommandId]);

	const processCommand = async (
		command: Command,
	): Promise<ExecutedCommand | FinishedCommand> => {
		setProcessingCommandId(undefined);
		setIsProcessing(true);

		const result = await execute(command);
		if (!('error' in result && result.error)) {
			setProcessingCommandId(result.id);
			return result;
		} else {
			setIsProcessing(false);
			return result;
		}
	};

	return {
		processCommand,
		isProcessing,
		processingCommandId,
	};
}
